import { db, type NomenclatureRow } from './db';

/**
 * At-rest obfuscation for sensitive nomenclature fields (physician names,
 * addresses/phones, prices).
 *
 * Uses a 256-bit AES-GCM key generated with `extractable: false` and stored as
 * a CryptoKey object in IndexedDB. A non-extractable key cannot be read out as
 * raw bytes from JS/devtools — so casual inspection of IndexedDB shows
 * ciphertext, not plaintext.
 *
 * NOTE: this is OBFUSCATION, not confidentiality. A determined attacker who
 * controls the browser can still call decrypt() from the console. The data is
 * public catalog data anyway; this just keeps it out of plain sight.
 */

const KEY_META = 'cryptoKey';
let keyPromise: Promise<CryptoKey> | null = null;

/**
 * Drop the in-memory key cache so the next `getKey()` re-reads (or regenerates +
 * persists) from `meta`. Call after wiping the DB, so a freshly-generated key is
 * persisted consistently with the data it encrypts — otherwise a stale in-memory
 * key would encrypt data that the next page load (reading `meta`) can't decrypt.
 */
export function resetKeyCache(): void {
  keyPromise = null;
}

function getKey(): Promise<CryptoKey> {
  if (keyPromise) return keyPromise;
  keyPromise = (async () => {
    const stored = await db.meta.get(KEY_META);
    if (stored?.value instanceof CryptoKey) return stored.value;
    const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, [
      'encrypt',
      'decrypt',
    ]);
    await db.meta.put({ key: KEY_META, value: key });
    return key;
  })();
  return keyPromise;
}

/** Encrypt the given fields of `row` into a single `iv||ciphertext` blob. */
export async function encryptFields(
  row: Record<string, unknown>,
  fields: string[],
): Promise<Uint8Array | undefined> {
  if (fields.length === 0) return undefined;
  const subset: Record<string, unknown> = {};
  for (const f of fields) subset[f] = row[f];

  const key = await getKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(JSON.stringify(subset)),
  );

  const out = new Uint8Array(12 + ct.byteLength);
  out.set(iv, 0);
  out.set(new Uint8Array(ct), 12);
  return out;
}

/** Decrypt an `iv||ciphertext` blob back to the original field map. */
export async function decryptBlob(blob: Uint8Array): Promise<Record<string, unknown>> {
  const key = await getKey();
  const iv = blob.subarray(0, 12) as BufferSource;
  const ct = blob.subarray(12) as BufferSource;
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
  return JSON.parse(new TextDecoder().decode(pt));
}

/** Merge a stored row's encrypted blob back into a plain object for use in the UI. */
export async function revealRow(row: NomenclatureRow): Promise<Record<string, unknown>> {
  if (!(row._enc instanceof Uint8Array)) {
    const { _enc, ...rest } = row;
    void _enc;
    return rest;
  }
  const decrypted = await decryptBlob(row._enc);
  const { _enc, ...rest } = row;
  void _enc;
  return { ...rest, ...decrypted };
}

/** Decrypt a list of rows. */
export function revealRows(rows: NomenclatureRow[]): Promise<Record<string, unknown>[]> {
  return Promise.all(rows.map(revealRow));
}
